import * as IF from "infrontjs";
import template from './template.html';

class ApiTokenList extends HTMLElement
{
    constructor()
    {
        super();
        this._isConnected = false;
        this._apiTokens = null;
    }

    connectedCallback()
    {
        if ( false === this._isConnected )
        {
            this.app = IF.App.get();
            this.render();
            this._isConnected = true;
        }
    }

    get apiTokens()
    {
        return this._apiTokens;
    }

    set apiTokens( val )
    {
        this._apiTokens = val;
        if ( this._isConnected )
        {
            this.render();
        }
    }

    _showLoadingIndicator()
    {
        const container = this.querySelector( '#api-tokens-container' );
        if ( container )
        {
            container.innerHTML = `
                <div class="d-flex align-items-center justify-content-center py-5">
                    <div class="spinner-border text-primary me-3" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span class="text-muted">Loading API tokens...</span>
                </div>
            `;
        }
    }

    async loadTokens()
    {
        this._showLoadingIndicator();

        try
        {
            const { result } = await this.app.api.get( '/apitoken' );

            if ( result.status === 200 && Array.isArray( result.data ) )
            {
                this._apiTokens = result.data;
                this._renderTokensTable();
            }
            else
            {
                const container = this.querySelector( '#api-tokens-container' );
                if ( container )
                {
                    container.innerHTML = '<p class="text-danger">Failed to load API tokens.</p>';
                }
            }
        }
        catch ( error )
        {
            const container = this.querySelector( '#api-tokens-container' );
            if ( container )
            {
                container.innerHTML = '<p class="text-danger">An error occurred while loading API tokens.</p>';
            }
        }
    }

    _renderTokensTable()
    {
        const container = this.querySelector( '#api-tokens-container' );
        if ( !container )
        {
            return;
        }

        if ( !this._apiTokens || this._apiTokens.length === 0 )
        {
            container.innerHTML = `
                <div class="text-center py-4">
                    <i class="ti ti-key-off display-6 text-muted mb-3"></i>
                    <p class="text-muted">No API tokens found.</p>
                </div>
            `;
            return;
        }

        let html = '<style>.api-token-cell { color: #eaeff4 !important; }</style>';
        html += '<div class="table-responsive"><table class="table table-hover">';
        html += `
            <thead>
                <tr>
                    <th class="text-muted text-uppercase fw-bold detail-label">Description</th>
                    <th class="text-muted text-uppercase fw-bold detail-label">Token</th>
                    <th class="text-muted text-uppercase fw-bold detail-label">Status</th>
                    <th class="text-muted text-uppercase fw-bold detail-label">Expires</th>
                    <th class="text-muted text-uppercase fw-bold detail-label">Created</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
        `;

        for ( const token of this._apiTokens )
        {
            const description = token.data?.description || '---';
            const fullToken = token.token || '---';
            const status = token.isExpired
                ? '<span class="badge bg-danger-subtle text-danger">Expired</span>'
                : '<span class="badge bg-success-subtle text-success">Active</span>';
            const expiresAt = token.expiresAt ? new Date( token.expiresAt ).toLocaleDateString() : 'Never';
            const createdAt = token.createdAt ? new Date( token.createdAt ).toLocaleDateString() : '---';

            html += `
                <tr>
                    <td class="fw-medium api-token-cell">${description}</td>
                    <td>
                        <code>${fullToken}</code>
                        <button type="button" class="btn btn-outline-secondary btn-sm ms-2" data-type="copy-api-token" data-token="${fullToken}" title="Copy to clipboard">
                            <i class="ti ti-copy"></i>
                        </button>
                    </td>
                    <td>${status}</td>
                    <td class="api-token-cell">${expiresAt}</td>
                    <td class="api-token-cell">${createdAt}</td>
                    <td>
                        <button type="button" class="btn btn-outline-primary btn-sm me-1" data-type="edit-api-token" data-token-id="${token.id}">
                            <i class="ti ti-pencil"></i>
                        </button>
                        <button type="button" class="btn btn-outline-danger btn-sm" data-type="delete-api-token" data-token-id="${token.id}">
                            <i class="ti ti-trash"></i>
                        </button>
                    </td>
                </tr>
            `;
        }

        html += '</tbody></table></div>';
        container.innerHTML = html;

        this._bindTableButtons();
    }

    _bindTableButtons()
    {
        const copyButtons = this.querySelectorAll( '[data-type="copy-api-token"]' );
        copyButtons.forEach( btn => {
            btn.addEventListener( 'click', ( e ) => {
                const token = e.currentTarget.dataset.token;
                this._copyTokenToClipboard( token );
            });
        });

        const editButtons = this.querySelectorAll( '[data-type="edit-api-token"]' );
        editButtons.forEach( btn => {
            btn.addEventListener( 'click', ( e ) => {
                const tokenId = e.currentTarget.dataset.tokenId;
                this._showEditModal( tokenId );
            });
        });

        const deleteButtons = this.querySelectorAll( '[data-type="delete-api-token"]' );
        deleteButtons.forEach( btn => {
            btn.addEventListener( 'click', ( e ) => {
                const tokenId = e.currentTarget.dataset.tokenId;
                this._deleteToken( tokenId );
            });
        });
    }

    async _copyTokenToClipboard( token )
    {
        try
        {
            await navigator.clipboard.writeText( token );
            this.app.view.toast( 'Token copied to clipboard', 'success' );
        }
        catch ( error )
        {
            this.app.view.toast( 'Failed to copy token to clipboard', 'error' );
        }
    }

    async _deleteToken( tokenId )
    {
        const confirmed = await this.app.view.confirm(
            'Delete API Token',
            'Are you sure you want to delete this API token? This action cannot be undone.'
        );

        if ( !confirmed )
        {
            return;
        }

        this._showLoadingIndicator();

        try
        {
            const { result } = await this.app.api.delete( `/apitoken/${tokenId}` );

            if ( result.status === 204 )
            {
                this.app.view.toast( 'API token deleted successfully', 'success' );
                this.loadTokens();
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to delete API token', 'error' );
                this._renderTokensTable();
            }
        }
        catch ( error )
        {
            this.app.view.toast( 'An error occurred while deleting API token', 'error' );
            this._renderTokensTable();
        }
    }

    render()
    {
        if ( !this.app?.view )
        {
            return;
        }

        this.app.view.render(
            this,
            template,
            {}
        );

        this._bindEvents();
    }

    _bindEvents()
    {
        // Create token button
        const createBtn = this.querySelector( '[data-type="create-api-token"]' );
        if ( createBtn )
        {
            createBtn.addEventListener( 'click', () => {
                this._showCreateModal();
            });
        }

        // Modal close button
        const closeBtn = this.querySelector( '[data-type="apitoken-modal-close"]' );
        if ( closeBtn )
        {
            closeBtn.addEventListener( 'click', () => {
                this._hideModal();
            });
        }

        // Modal cancel button
        const cancelBtn = this.querySelector( '[data-type="apitoken-modal-cancel"]' );
        if ( cancelBtn )
        {
            cancelBtn.addEventListener( 'click', () => {
                this._hideModal();
            });
        }

        // Modal save button
        const saveBtn = this.querySelector( '[data-type="apitoken-modal-save"]' );
        if ( saveBtn )
        {
            saveBtn.addEventListener( 'click', () => {
                this._createToken();
            });
        }

        // Never expires checkbox
        const neverExpiresCheckbox = this.querySelector( '#create-apitoken-never-expires' );
        if ( neverExpiresCheckbox )
        {
            neverExpiresCheckbox.addEventListener( 'change', () => {
                this._toggleExpirationDateField();
            });
        }

        // Edit modal close button
        const editCloseBtn = this.querySelector( '[data-type="edit-apitoken-modal-close"]' );
        if ( editCloseBtn )
        {
            editCloseBtn.addEventListener( 'click', () => {
                this._hideEditModal();
            });
        }

        // Edit modal cancel button
        const editCancelBtn = this.querySelector( '[data-type="edit-apitoken-modal-cancel"]' );
        if ( editCancelBtn )
        {
            editCancelBtn.addEventListener( 'click', () => {
                this._hideEditModal();
            });
        }

        // Edit modal save button
        const editSaveBtn = this.querySelector( '[data-type="edit-apitoken-modal-save"]' );
        if ( editSaveBtn )
        {
            editSaveBtn.addEventListener( 'click', () => {
                this._updateToken();
            });
        }

        // Edit never expires checkbox
        const editNeverExpiresCheckbox = this.querySelector( '#edit-apitoken-never-expires' );
        if ( editNeverExpiresCheckbox )
        {
            editNeverExpiresCheckbox.addEventListener( 'change', () => {
                this._toggleEditExpirationDateField();
            });
        }
    }

    _showCreateModal()
    {
        // Reset form
        const form = this.querySelector( '#create-api-token-form' );
        if ( form )
        {
            form.reset();
        }

        // Reset checkbox and hide expiration date field
        const neverExpiresCheckbox = this.querySelector( '#create-apitoken-never-expires' );
        if ( neverExpiresCheckbox )
        {
            neverExpiresCheckbox.checked = true;
        }

        const expirationContainer = this.querySelector( '#expiration-date-container' );
        if ( expirationContainer )
        {
            expirationContainer.style.display = 'none';
        }

        const modal = this.querySelector( '#createApiTokenModal' );
        if ( modal )
        {
            modal.classList.add( 'show' );
            modal.style.display = 'block';
            document.body.classList.add( 'modal-open' );

            const backdrop = document.createElement( 'div' );
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = 'createApiTokenModalBackdrop';
            document.body.appendChild( backdrop );
        }
    }

    _hideModal()
    {
        const modal = this.querySelector( '#createApiTokenModal' );
        const backdrop = document.getElementById( 'createApiTokenModalBackdrop' );

        if ( modal )
        {
            modal.classList.remove( 'show' );
            modal.style.display = 'none';
            document.body.classList.remove( 'modal-open' );
        }

        if ( backdrop )
        {
            backdrop.remove();
        }
    }

    _toggleExpirationDateField()
    {
        const neverExpiresCheckbox = this.querySelector( '#create-apitoken-never-expires' );
        const expirationContainer = this.querySelector( '#expiration-date-container' );

        if ( neverExpiresCheckbox && expirationContainer )
        {
            expirationContainer.style.display = neverExpiresCheckbox.checked ? 'none' : 'block';
        }
    }

    async _createToken()
    {
        const form = this.querySelector( '#create-api-token-form' );
        const descriptionInput = form?.querySelector( '#create-apitoken-description' );
        const neverExpiresCheckbox = form?.querySelector( '#create-apitoken-never-expires' );
        const expiresInput = form?.querySelector( '#create-apitoken-expires' );

        // Validate: if not "never expires", expiration date is required
        if ( !neverExpiresCheckbox?.checked && !expiresInput?.value )
        {
            this.app.view.toast( 'Please select an expiration date', 'error' );
            return;
        }

        // Validate: expiration date must be in the future
        if ( !neverExpiresCheckbox?.checked && expiresInput?.value )
        {
            const expirationDate = new Date( expiresInput.value );
            if ( expirationDate <= new Date() )
            {
                this.app.view.toast( 'Expiration date must be in the future', 'error' );
                return;
            }
        }

        const payload = {};

        // Add description to data if provided
        const description = descriptionInput?.value?.trim();
        if ( description )
        {
            payload.data = { description };
        }

        // Add expiration date if not "never expires"
        if ( !neverExpiresCheckbox?.checked && expiresInput?.value )
        {
            payload.expiresAt = new Date( expiresInput.value ).toISOString();
        }

        try
        {
            const { result } = await this.app.api.post( '/apitoken', payload );

            if ( result.status === 201 && result.data )
            {
                this._hideModal();
                this.app.view.toast( 'API token created successfully', 'success' );
                this.loadTokens();
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to create API token', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.toast( 'An error occurred while creating API token', 'error' );
        }
    }

    _showEditModal( tokenId )
    {
        const token = this._apiTokens?.find( t => t.id === tokenId );
        if ( !token )
        {
            return;
        }

        this._editingTokenId = tokenId;

        // Set form values
        const descriptionInput = this.querySelector( '#edit-apitoken-description' );
        if ( descriptionInput )
        {
            descriptionInput.value = token.data?.description || '';
        }

        const neverExpiresCheckbox = this.querySelector( '#edit-apitoken-never-expires' );
        const expirationContainer = this.querySelector( '#edit-expiration-date-container' );
        const expiresInput = this.querySelector( '#edit-apitoken-expires' );

        if ( token.expiresAt )
        {
            if ( neverExpiresCheckbox )
            {
                neverExpiresCheckbox.checked = false;
            }
            if ( expirationContainer )
            {
                expirationContainer.style.display = 'block';
            }
            if ( expiresInput )
            {
                expiresInput.value = new Date( token.expiresAt ).toISOString().split( 'T' )[ 0 ];
            }
        }
        else
        {
            if ( neverExpiresCheckbox )
            {
                neverExpiresCheckbox.checked = true;
            }
            if ( expirationContainer )
            {
                expirationContainer.style.display = 'none';
            }
            if ( expiresInput )
            {
                expiresInput.value = '';
            }
        }

        const modal = this.querySelector( '#editApiTokenModal' );
        if ( modal )
        {
            modal.classList.add( 'show' );
            modal.style.display = 'block';
            document.body.classList.add( 'modal-open' );

            const backdrop = document.createElement( 'div' );
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = 'editApiTokenModalBackdrop';
            document.body.appendChild( backdrop );
        }
    }

    _hideEditModal()
    {
        const modal = this.querySelector( '#editApiTokenModal' );
        const backdrop = document.getElementById( 'editApiTokenModalBackdrop' );

        if ( modal )
        {
            modal.classList.remove( 'show' );
            modal.style.display = 'none';
            document.body.classList.remove( 'modal-open' );
        }

        if ( backdrop )
        {
            backdrop.remove();
        }

        this._editingTokenId = null;
    }

    _toggleEditExpirationDateField()
    {
        const neverExpiresCheckbox = this.querySelector( '#edit-apitoken-never-expires' );
        const expirationContainer = this.querySelector( '#edit-expiration-date-container' );

        if ( neverExpiresCheckbox && expirationContainer )
        {
            expirationContainer.style.display = neverExpiresCheckbox.checked ? 'none' : 'block';
        }
    }

    async _updateToken()
    {
        if ( !this._editingTokenId )
        {
            return;
        }

        const form = this.querySelector( '#edit-api-token-form' );
        const descriptionInput = form?.querySelector( '#edit-apitoken-description' );
        const neverExpiresCheckbox = form?.querySelector( '#edit-apitoken-never-expires' );
        const expiresInput = form?.querySelector( '#edit-apitoken-expires' );

        // Validate: if not "never expires", expiration date is required
        if ( !neverExpiresCheckbox?.checked && !expiresInput?.value )
        {
            this.app.view.toast( 'Please select an expiration date', 'error' );
            return;
        }

        // Validate: expiration date must be in the future
        if ( !neverExpiresCheckbox?.checked && expiresInput?.value )
        {
            const expirationDate = new Date( expiresInput.value );
            if ( expirationDate <= new Date() )
            {
                this.app.view.toast( 'Expiration date must be in the future', 'error' );
                return;
            }
        }

        const payload = {
            description: descriptionInput?.value?.trim() || null,
            expiresAt: neverExpiresCheckbox?.checked ? null : new Date( expiresInput.value ).toISOString()
        };

        try
        {
            const { result } = await this.app.api.patch( `/apitoken/${this._editingTokenId}`, payload );

            if ( result.status === 200 )
            {
                this._hideEditModal();
                this.app.view.toast( 'API token updated successfully', 'success' );
                this.loadTokens();
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to update API token', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.toast( 'An error occurred while updating API token', 'error' );
        }
    }
}

customElements.define( 'api-token-list', ApiTokenList );
